import { Check, X, ListChecks, FileCode } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PxMark } from '@/components/PxMark';

export interface PlanStep {
    title: string;
    description?: string;
}

export interface PlanData {
    id: string;
    title: string;
    steps: PlanStep[];
    estimatedFiles: string[];
    status: 'pending' | 'confirmed' | 'rejected';
}

interface PlanCardProps {
    plan: PlanData;
    onConfirm?: () => void;
    onReject?: () => void;
    isLoading?: boolean;
}

export function PlanCard({ plan, onConfirm, onReject, isLoading }: PlanCardProps) {
    const isPending = plan.status === 'pending';
    const isRejected = plan.status === 'rejected';
    const isConfirmed = plan.status === 'confirmed';

    return (
        <div
            className={cn(
                "border border-border bg-card transition-opacity duration-150",
                isRejected && "opacity-50"
            )}
        >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <div className="h-7 w-7 bg-secondary flex items-center justify-center shrink-0">
                    <ListChecks className={cn(
                        "w-4 h-4",
                        isRejected ? "text-faint" : "text-foreground"
                    )} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className={cn(
                        "text-sm font-medium text-foreground",
                        isRejected && "text-faint line-through"
                    )}>
                        {plan.title}
                    </h3>
                    <p className="font-mono text-[11px] text-faint">
                        {plan.steps.length} étapes · {plan.estimatedFiles.length} fichier{plan.estimatedFiles.length > 1 ? 's' : ''}
                    </p>
                </div>

                {/* Status badge */}
                {isConfirmed && (
                    <span className="bg-ok/10 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-ok">
                        Confirmé
                    </span>
                )}
                {isRejected && (
                    <span className="bg-secondary px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-faint">
                        Modifié
                    </span>
                )}
            </div>

            {/* Steps */}
            <div className="px-4 py-3 space-y-2">
                {plan.steps.map((step, index) => (
                    <div key={index} className="flex items-start gap-3">
                        <span className={cn(
                            "flex-shrink-0 w-5 pt-0.5 text-right font-mono text-[11px] tabular-nums",
                            isPending && "text-muted-foreground",
                            isRejected && "text-faint",
                            isConfirmed && "text-ok"
                        )}>
                            {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className={cn(
                                "text-sm text-foreground",
                                isRejected && "text-faint line-through"
                            )}>
                                {step.title}
                            </p>
                            {step.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {step.description}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Estimated files */}
            {plan.estimatedFiles.length > 0 && (
                <div className="px-4 py-2 border-t border-border">
                    <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                        Fichiers à générer
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {plan.estimatedFiles.map((file, index) => (
                            <span
                                key={index}
                                className={cn(
                                    "inline-flex items-center gap-1 border border-border px-1.5 py-0.5 font-mono text-[10px]",
                                    isPending && "bg-secondary text-muted-foreground",
                                    isRejected && "bg-secondary text-faint line-through",
                                    isConfirmed && "bg-ok/10 text-ok"
                                )}
                            >
                                <FileCode className="w-2.5 h-2.5" />
                                {file.split('/').pop()}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Actions - only show for pending plans */}
            {isPending && !isLoading && (
                <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
                    <button
                        onClick={onConfirm}
                        className="flex-1 flex items-center justify-center gap-2 bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                        <Check className="w-3.5 h-3.5" />
                        Confirmer et exécuter
                    </button>
                    <button
                        onClick={onReject}
                        className="flex items-center justify-center gap-2 border border-border bg-card px-3 py-2 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                    >
                        <X className="w-3.5 h-3.5" />
                        Modifier
                    </button>
                </div>
            )}

            {/* Loading state */}
            {isPending && isLoading && (
                <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-border text-muted-foreground text-xs">
                    <PxMark size={12} state="generating" />
                    <span>Exécution du plan…</span>
                </div>
            )}
        </div>
    );
}

// Parser function to extract plan from AI response
export function parsePlan(content: string): PlanData | null {
    const planMatch = content.match(/<pixelPlan>([\s\S]*?)<\/pixelPlan>/);
    if (!planMatch) return null;

    const planContent = planMatch[1];

    // Extract title
    const titleMatch = planContent.match(/<title>([\s\S]*?)<\/title>/);
    const title = titleMatch ? (titleMatch[1] ?? '').trim() || 'Plan de développement' : 'Plan de développement';

    // Extract steps
    const stepsMatch = planContent.match(/<steps>([\s\S]*?)<\/steps>/);
    const steps: PlanStep[] = [];
    if (stepsMatch) {
        const stepMatches = stepsMatch[1].matchAll(/<step>([\s\S]*?)<\/step>/g);
        for (const match of stepMatches) {
            steps.push({ title: (match[1] ?? '').trim() || 'Étape' });
        }
    }

    // Extract files
    const filesMatch = planContent.match(/<files>([\s\S]*?)<\/files>/);
    const estimatedFiles: string[] = [];
    if (filesMatch) {
        const fileMatches = filesMatch[1].matchAll(/<file>([\s\S]*?)<\/file>/g);
        for (const match of fileMatches) {
            const file = (match[1] ?? '').trim();
            if (file) estimatedFiles.push(file);
        }
    }

    return {
        id: crypto.randomUUID(),
        title,
        steps,
        estimatedFiles,
        status: 'pending'
    };
}
